import { useState } from 'react'
import {
  approveToil,
  authoriseToil,
  hours,
  loadToilBoard,
  rejectToil,
  setToilPayBasis,
  unauthoriseToil,
  type ToilEvent,
} from '@/data/toil'
import { useAsync } from '@/data/useAsync'
import { Card, Failed, Loading, Tile } from '@/components/bits'
import { shortDate, timeOfDay } from '@/lib/format'
import { orgName, useOrg } from '@/org/OrgProvider'

/**
 * The TOIL board.
 *
 * A queue, not a form. HOTEL1 works about 44 approved extensions a month
 * across 52 people, so this is somebody's ongoing work rather than an
 * occasional errand, and it is laid out for working through.
 *
 * Nothing here enforces a rule. The database does: a reason is a check
 * constraint, an authorisation must be a different person from the approval,
 * and the authorised hours are frozen at the moment of signing. This screen's
 * job is to make the rules legible before somebody hits them.
 */

function Reason({
  label,
  action,
  onDone,
}: {
  label: string
  action: (reason: string) => Promise<void>
  onDone: () => void
}) {
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!open) {
    return (
      <button type="button" className="ghost" onClick={() => setOpen(true)}>
        {label}
      </button>
    )
  }

  return (
    <form
      className="inlineform"
      onSubmit={async (e) => {
        e.preventDefault()
        setBusy(true)
        setError(null)
        try {
          await action(reason.trim())
          setOpen(false)
          setReason('')
          onDone()
        } catch (cause) {
          setError(cause instanceof Error ? cause.message : String(cause))
        } finally {
          setBusy(false)
        }
      }}
    >
      <input
        autoFocus
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Why? This is recorded against the hours."
      />
      <div className="actions">
        <button type="submit" disabled={busy || reason.trim().length === 0}>
          {busy ? 'Saving' : label}
        </button>
        <button type="button" className="ghost" onClick={() => setOpen(false)} disabled={busy}>
          Cancel
        </button>
      </div>
      {error !== null && <p className="callout">{error}</p>}
    </form>
  )
}

function Plain({
  label,
  action,
  onDone,
}: {
  label: string
  action: () => Promise<void>
  onDone: () => void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  return (
    <>
      <button
        type="button"
        disabled={busy}
        onClick={async () => {
          setBusy(true)
          setError(null)
          try {
            await action()
            onDone()
          } catch (cause) {
            setError(cause instanceof Error ? cause.message : String(cause))
          } finally {
            setBusy(false)
          }
        }}
      >
        {busy ? 'Saving' : label}
      </button>
      {error !== null && <p className="callout">{error}</p>}
    </>
  )
}

function Row({
  ev,
  reload,
  canAuthorise,
  canSettlePayBasis,
  organisationId,
}: {
  ev: ToilEvent
  reload: () => void
  canAuthorise: boolean
  canSettlePayBasis: boolean
  organisationId: string
}) {
  return (
    <div className="stackfield">
      <div>
        <strong>{ev.fullName}</strong>
        {ev.departmentName !== null && <span className="muted"> · {ev.departmentName}</span>}
        <span className="muted">
          {' '}
          · {shortDate(ev.shiftDate)} · {hours(ev.hours)}
        </span>
        {ev.isLong && (
          <span className="callout">
            {' '}
            {hours(ev.hours)} is long for an extension. Worth checking the shift before it becomes a
            balance.
          </span>
        )}
      </div>

      {/* Provenance, not decoration. An inference from a holiday account and a
          decision somebody made are different things, and only one of them is
          worth defending in a pay dispute. */}
      {ev.payBasis !== 'unclear' && (
        <p className="muted">
          {ev.payBasis === 'salaried' ? 'Salaried' : 'Paid hourly'}
          {ev.payBasisSource === 'set_by_person' ? '' : ', inferred'}
          {ev.payBasisWhy === null ? '' : `: ${ev.payBasisWhy}`}
        </p>
      )}

      {ev.hodReason !== null && (
        <p>
          <span className="muted">Reason: </span>
          {ev.hodReason}
        </p>
      )}
      {ev.hodApprovedAt !== null && (
        <p className="muted">
          Approved {timeOfDay(ev.hodApprovedAt)} on {shortDate(ev.hodApprovedAt)}
          {ev.hodApprovedByEmail !== null && ` by ${ev.hodApprovedByEmail}`}
        </p>
      )}
      {ev.authorisedAt !== null && (
        <p className="muted">
          Authorised {hours(ev.authorisedHours)} on {shortDate(ev.authorisedAt)}
          {ev.authorisedByEmail !== null && ` by ${ev.authorisedByEmail}`}
          {/* Not hidden. One signature and two signatures are different things
              and somebody reviewing these later should be able to see which. */}
          {ev.singleSignature && ', who approved it as well, so this went through on one signature'}
        </p>
      )}

      {/* The frozen figure and the live one have parted company. The balance is
          unaffected, which is the point of freezing it, but somebody changed a
          shift after it was signed off. */}
      {ev.hoursChangedSinceAuthorisation && (
        <p className="callout">
          This shift now reads {hours(ev.hours)} in Planday but {hours(ev.authorisedHours)} was
          authorised. The authorised figure is what counts and has not changed.
        </p>
      )}

      {ev.shiftWriteStatus === 'failed' && ev.shiftWriteError !== null && (
        <p className="callout">Planday refused the change: {ev.shiftWriteError}</p>
      )}
      {ev.shiftWriteStatus === 'blocked' && (
        <p className="muted">
          Waiting to reach Planday. Writing TOIL shift types is switched off for this hotel.
        </p>
      )}

      {ev.rejectedReason !== null && (
        <p>
          <span className="muted">Refused: </span>
          {ev.rejectedReason}
        </p>
      )}

      <div className="actions">
        {ev.status === 'observed' && ev.payBasis === 'salaried' && (
          <>
            <Reason label="Approve" action={(r) => approveToil(ev.id, r)} onDone={reload} />
            <Reason label="Refuse" action={(r) => rejectToil(ev.id, r)} onDone={reload} />
          </>
        )}

        {ev.status === 'observed' && ev.payBasis === 'unclear' && (
          <>
            {/* The reason comes from the rule that decided it, not from this
                screen. "Holds both kinds of account" and "the portal has no
                accounts at all" both read as unclear and are different
                problems. */}
            <span className="muted">
              Their pay basis is unclear, so this cannot be approved yet
              {ev.payBasisWhy === null ? '.' : `: ${ev.payBasisWhy}.`}
            </span>
            {canSettlePayBasis ? (
              <>
                <Plain
                  label="They are salaried"
                  action={() => setToilPayBasis(organisationId, ev.employmentId, 'salaried').then(() => undefined)}
                  onDone={reload}
                />
                <Plain
                  label="They are paid hourly"
                  action={() => setToilPayBasis(organisationId, ev.employmentId, 'hourly').then(() => undefined)}
                  onDone={reload}
                />
              </>
            ) : (
              /* Settling a pay basis needs hr, org_admin or group_admin. A
                 manager pressing this would be refused by the database, and a
                 button that is certain to fail is worse than no button. */
              <span className="muted">Only HR can settle this.</span>
            )}
          </>
        )}

        {ev.status === 'hod_approved' &&
          (canAuthorise ? (
            <>
              <Plain label="Authorise" action={() => authoriseToil(ev.id)} onDone={reload} />
              <Reason label="Refuse" action={(r) => rejectToil(ev.id, r)} onDone={reload} />
            </>
          ) : (
            <span className="muted">Waiting for HR or the GM to authorise.</span>
          ))}

        {ev.status === 'authorised' && canAuthorise && (
          <Reason
            label="Withdraw authorisation"
            action={(r) => unauthoriseToil(ev.id, r)}
            onDone={reload}
          />
        )}
      </div>
    </div>
  )
}

function Section({
  title,
  blurb,
  events,
  reload,
  canAuthorise,
  canSettlePayBasis,
  organisationId,
}: {
  title: string
  blurb: string
  events: ToilEvent[]
  reload: () => void
  canAuthorise: boolean
  canSettlePayBasis: boolean
  organisationId: string
}) {
  if (events.length === 0) return null
  return (
    <Card title={`${title} (${events.length})`}>
      <p className="muted">{blurb}</p>
      {events.map((ev) => (
        <Row
          key={ev.id}
          ev={ev}
          reload={reload}
          canAuthorise={canAuthorise}
          canSettlePayBasis={canSettlePayBasis}
          organisationId={organisationId}
        />
      ))}
    </Card>
  )
}

export function Toil() {
  const { current } = useOrg()
  const board = useAsync(() => loadToilBoard(current.id), [current.id])

  if (board.state === 'loading') return <Loading what="TOIL" />
  if (board.state === 'error') return <Failed message={board.message} onRetry={board.reload} />
  const d = board.data

  const nothing =
    d.awaitingHod.length === 0 &&
    d.awaitingAuthorisation.length === 0 &&
    d.payBasisUnclear.length === 0 &&
    d.paidExtensions.length === 0 &&
    d.authorised.length === 0

  return (
    <>
      <div className="tiles">
        <Tile label="Waiting on a head of department" value={String(d.awaitingHod.length)} />
        <Tile label="Waiting to be authorised" value={String(d.awaitingAuthorisation.length)} />
        <Tile label="Pay basis unclear" value={String(d.payBasisUnclear.length)} />
        {/* Both lists, because they are now exclusive: the tile counts all
            authorised hours whether or not Planday has credited them yet. */}
        <Tile
          label="Authorised"
          value={hours(
            [...d.authorised, ...d.creditNotSeen].reduce((a, e) => a + (e.authorisedHours ?? 0), 0),
          )}
        />
        {/* Two separate counts, because "we have not written to Planday" and
            "Planday has not credited it" are different failures with different
            fixes, and one number covering both would hide whichever is smaller. */}
        {/* Named for what it is. These hours are not paid until somebody
            changes the shift type to Paid Overtime in Planday, which this
            system does not do. Calling it "paid extensions" implied it was
            handled. */}
        <Tile label="Hourly, not yet paid" value={String(d.paidExtensions.length)} />
        <Tile label="Not yet written to Planday" value={String(d.writeOutstanding.length)} />
        <Tile label="Credit not seen in Planday" value={String(d.creditNotSeen.length)} />
      </div>

      {nothing && (
        <Card title="Nothing to do">
          <p>
            No shift extensions have been recorded since TOIL was switched on for{' '}
            {orgName(current)}. The scan looks at approved Shift Extension shifts only,
            and only from go-live onwards, so anything worked before that is not here by design.
          </p>
        </Card>
      )}

      <Section
        title="Waiting on a head of department"
        blurb="An approved extension needs a reason and an approval before it can be authorised."
        events={d.awaitingHod}
        reload={board.reload}
        canAuthorise={d.canAuthorise}
        canSettlePayBasis={d.canSettlePayBasis}
        organisationId={current.id}
      />

      <Section
        title="Waiting to be authorised"
        blurb={
          d.canAuthorise
            ? 'Approved by a head of department. Authorising freezes the hours and relabels the shift in Planday as TOIL Worked.'
            : 'Approved by a head of department, and waiting for HR or the GM. Only they can take the second step.'
        }
        events={d.awaitingAuthorisation}
        reload={board.reload}
        canAuthorise={d.canAuthorise}
        canSettlePayBasis={d.canSettlePayBasis}
        organisationId={current.id}
      />

      <Section
        title="Pay basis unclear"
        blurb={
          d.canSettlePayBasis
            ? "Planday cannot say whether these people are salaried or hourly, and the reason is on each row. Nobody guesses: getting it wrong either pays somebody twice or takes away overtime they were owed."
            : "Planday cannot say whether these people are salaried or hourly, and the reason is on each row. HR settles it, once per person, before their extensions can be approved."
        }
        events={d.payBasisUnclear}
        reload={board.reload}
        canAuthorise={d.canAuthorise}
        canSettlePayBasis={d.canSettlePayBasis}
        organisationId={current.id}
      />

      <Section
        title="Credit not seen in Planday"
        blurb="Authorised, and the shift was relabelled, but no matching credit has been found on the person's TOIL account. Planday usually creates one by itself, and on HOTEL1 it did so for 42 of 49 TOIL shifts. These are the rest."
        events={d.creditNotSeen}
        reload={board.reload}
        canAuthorise={d.canAuthorise}
        canSettlePayBasis={d.canSettlePayBasis}
        organisationId={current.id}
      />

      <Section
        title="Hourly, not yet paid"
        blurb="Worked by people paid hourly, so these are not TOIL. They are also NOT YET PAID: an hourly person is paid for an extension only once its shift type is changed to Paid Overtime in Planday, and until somebody does that these hours reach nobody. TFH-HR does not make that change."
        events={d.paidExtensions}
        reload={board.reload}
        canAuthorise={d.canAuthorise}
        canSettlePayBasis={d.canSettlePayBasis}
        organisationId={current.id}
      />

      <Section
        title="Authorised"
        blurb="Hours owed. The figure shown is the one frozen when it was signed off, not what the shift says in Planday today. Where HR or the GM approved an extension themselves, that approval was the authorisation and the row says so."
        events={d.authorised}
        reload={board.reload}
        canAuthorise={d.canAuthorise}
        canSettlePayBasis={d.canSettlePayBasis}
        organisationId={current.id}
      />
    </>
  )
}
